import { presenceRowIsXpro4 } from "./teamPresenceUsersSnapshot";

/**
 * Normaliza o payload do evento socket `users` (formatos diferentes no mesmo servidor).
 * - `{ queue_id, data: [ ... ] }` (lista)
 * - `{ queue_id, data: { id, ... } }` (um objeto)
 * - `{ id, name, workspace, client?, ... }` (objeto único por emissão / eco)
 */
export function normalizeUsersSocketUsersPayload(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data != null && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    const id = Number(payload.data.id);
    if (Number.isFinite(id)) return [payload.data];
  }
  if (typeof payload === "object" && Number.isFinite(Number(payload.id))) {
    if (payload.queue_id != null && payload.data == null) return [];
    return [payload];
  }
  return [];
}

/** Cache de emissões locais (antes do servidor replicar telemetria completa). */
const localPresenceEmitCache = new Map();

/**
 * Guarda o payload que esta aba acabou de emitir (device/browser/version locais).
 * @param {object} payload
 */
export function rememberLocalPresenceEmit(payload) {
  if (!payload || typeof payload !== "object") return;
  const uid = Number(payload.id);
  const tab = payload.tab;
  if (Number.isFinite(uid) && tab != null) {
    localPresenceEmitCache.set(`uidtab:${uid}:${String(tab)}`, { ...payload });
  }
  const sid = payload.socketId != null ? String(payload.socketId).trim() : "";
  if (sid) localPresenceEmitCache.set(`sid:${sid}`, { ...payload });
  while (localPresenceEmitCache.size > 900) {
    const first = localPresenceEmitCache.keys().next().value;
    if (first == null) break;
    localPresenceEmitCache.delete(first);
  }
}

function lookupRichPresenceRow(row, cache) {
  const sid = row?.socketId != null ? String(row.socketId).trim() : "";
  if (sid) {
    if (cache.has(`sid:${sid}`)) return cache.get(`sid:${sid}`);
    if (localPresenceEmitCache.has(`sid:${sid}`)) return localPresenceEmitCache.get(`sid:${sid}`);
  }
  const uid = Number(row?.id);
  const tab = row?.tab;
  if (Number.isFinite(uid) && tab != null) {
    const key = `uidtab:${uid}:${String(tab)}`;
    if (cache.has(key)) return cache.get(key);
    if (localPresenceEmitCache.has(key)) return localPresenceEmitCache.get(key);
  }
  return null;
}

/**
 * Mescla linhas “mínimas” (id, name, workspace, socketId) com a última linha rica do mesmo socket/tab.
 * @param {object[]} rows
 * @param {Map<string, object>} cache — chaves `sid:…`, `uidtab:…`
 */
export function enrichPresenceRowsFromSocketCache(rows, cache) {
  return rows.map((row) => {
    const rich = lookupRichPresenceRow(row, cache);
    if (rich && (presenceRowIsXpro4(row) || presenceRowIsXpro4(rich))) {
      return mergeRichIntoRow(row, rich);
    }
    return row;
  });
}

function mergeRichIntoRow(row, rich) {
  return {
    ...row,
    client: rich.client ?? row.client,
    app: rich.app ?? row.app,
    tab: rich.tab ?? row.tab,
    path: rich.path ?? row.path,
    since: rich.since ?? row.since,
    lastActivityAt: rich.lastActivityAt ?? row.lastActivityAt,
    focused: rich.focused !== undefined ? rich.focused : row.focused,
    deviceType: rich.deviceType ?? rich.device_type ?? row.deviceType ?? row.device_type,
    browserName: rich.browserName ?? rich.browser_name ?? row.browserName ?? row.browser_name,
    appVersion: rich.appVersion ?? rich.app_version ?? row.appVersion ?? row.app_version,
    appTheme: rich.appTheme ?? rich.app_theme ?? row.appTheme ?? row.app_theme,
  };
}

/**
 * Guarda linhas Xpro 4 completas para cruzar com snapshots enxutos posteriores.
 * @param {object[]} rows
 * @param {Map<string, object>} cache
 */
export function rememberRichPresenceRowsInCache(rows, cache, { maxKeys = 900 } = {}) {
  for (const row of rows) {
    if (!presenceRowIsXpro4(row)) continue;
    const sid = row?.socketId != null ? String(row.socketId).trim() : "";
    if (sid) cache.set(`sid:${sid}`, { ...row });
    const uid = Number(row?.id);
    if (Number.isFinite(uid) && row.tab != null) {
      cache.set(`uidtab:${uid}:${String(row.tab)}`, { ...row });
    }
  }
  while (cache.size > maxKeys) {
    const first = cache.keys().next().value;
    if (first == null) break;
    cache.delete(first);
  }
}

/**
 * Normaliza, atualiza cache com linhas ricas do próprio pacote, enriquece mínimas e atualiza o cache de novo.
 * @param {object} payload
 * @param {Map<string, object>} cache
 */
export function prepareUsersSocketRowsForPresence(payload, cache) {
  const rows = normalizeUsersSocketUsersPayload(payload);
  rememberRichPresenceRowsInCache(rows, cache);
  const enriched = enrichPresenceRowsFromSocketCache(rows, cache);
  rememberRichPresenceRowsInCache(enriched, cache);
  return enriched;
}

/** Cache compartilhado: várias telas montam `useTeamSocketPresence` e o cache não pode morrer na desmontagem. */
const mainAppPresenceSocketMergeCache = new Map();

export function mergePresenceSocketRowsForMainApp(payload) {
  return prepareUsersSocketRowsForPresence(payload, mainAppPresenceSocketMergeCache);
}

export function clearMainAppPresenceSocketMergeCache() {
  mainAppPresenceSocketMergeCache.clear();
  localPresenceEmitCache.clear();
}
